import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { Disc3, Target, UtensilsCrossed, CircleDot, ArrowRight, Star, Quote, Clock, Martini, Instagram } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { IMAGES, HOURS, INSTAGRAM_POSTS } from "../i18n/translations";
import { Reveal, MaskLine, SectionHeading, Sparkles } from "../components/ui-bits";
import { Marquee } from "../components/Marquee";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center justify-center overflow-hidden grain" data-testid="hero">
      <motion.div className="absolute inset-0 scale-110" style={{ y: bgY }}>
        <img src={IMAGES.lanesReal} alt="Bowling Helios" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#05070B]/80 via-[#05070B]/60 to-[#05070B]" />
      <div className="light-beam left-[8%]" />
      <div className="light-beam right-[8%]" style={{ animationDelay: "-5s" }} />
      <Sparkles count={18} />

      <motion.div className="relative z-10 text-center px-5 pt-24" style={{ opacity: fade }}>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="font-display text-sm sm:text-base tracking-[0.5em] uppercase text-[#F0C971] mb-6"
          data-testid="hero-eyebrow"
        >
          {t("hero.eyebrow")}
        </motion.p>
        <h1 className="leading-none">
          <MaskLine delay={0.35}>
            <span className="font-brand block text-[11vw] sm:text-8xl lg:text-9xl neon-rainbow uppercase" data-testid="hero-title-1">
              {t("hero.title1")}
            </span>
          </MaskLine>
          <MaskLine delay={0.55}>
            <span className="font-brand block text-[11vw] sm:text-8xl lg:text-9xl gold-text uppercase" data-testid="hero-title-2">
              {t("hero.title2")}
            </span>
          </MaskLine>
        </h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 1 }}
          className="mt-6 text-lg sm:text-xl italic text-[#E2F1FF]"
          data-testid="hero-tagline"
        >
          {t("hero.tagline")}
        </motion.p>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 1 }}
          className="mt-2 text-sm sm:text-base text-[#9db8d6]"
          data-testid="hero-sub"
        >
          {t("hero.sub")}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link to="/reserveer" data-testid="hero-reserve-cta" className="btn-neon font-display text-lg tracking-[0.25em] uppercase px-12 py-4 rounded-full">
            {t("hero.cta1")}
          </Link>
          <Link to="/arrangementen" data-testid="hero-arrangements-cta" className="btn-ghost font-display text-lg tracking-[0.25em] uppercase px-12 py-4 rounded-full">
            {t("hero.cta2")}
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#9db8d6] text-xs tracking-[0.4em] uppercase"
      >
        {t("hero.scroll")}
      </motion.div>
    </section>
  );
};

const QuickInfo = () => {
  const { t } = useLanguage();
  const cards = [
    { icon: Disc3, title: t("quick.lanes"), text: t("quick.lanesText"), testid: "quick-lanes" },
    { icon: CircleDot, title: t("quick.pool"), text: t("quick.poolText"), testid: "quick-pool" },
    { icon: Target, title: t("quick.darts"), text: t("quick.dartsText"), testid: "quick-darts" },
    { icon: UtensilsCrossed, title: t("quick.food"), text: t("quick.foodText"), testid: "quick-food" },
    { icon: Martini, title: t("quick.cocktail"), text: t("quick.cocktailText"), testid: "quick-cocktail" },
  ];
  return (
    <section className="relative max-w-7xl mx-auto px-5 -mt-24 z-20 pb-24" data-testid="quick-info">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        {cards.map((c, i) => (
          <Reveal key={c.testid} delay={i * 0.1}>
            <div data-testid={c.testid} className="glass glow-card rounded-2xl p-6 sm:p-8 text-center h-full">
              <c.icon className="mx-auto text-[#00F0FF] mb-4 drop-shadow-[0_0_12px_rgba(0,240,255,0.6)]" size={36} strokeWidth={1.5} />
              <h3 className="font-display text-xl sm:text-2xl uppercase tracking-wider neon">{c.title}</h3>
              <p className="mt-2 text-xs sm:text-sm text-[#9db8d6]">{c.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const chapters = [
  { num: "01", titleKey: "manifesto.c1t", textKey: "manifesto.c1x", img: IMAGES.lanesReal, to: "/bowling", testid: "chapter-bowling" },
  { num: "02", titleKey: "manifesto.c2t", textKey: "manifesto.c2x", img: IMAGES.buffetReal, to: "/arrangementen", testid: "chapter-arrangements" },
  { num: "03", titleKey: "manifesto.c4t", textKey: "manifesto.c4x", img: IMAGES.jeneverShots, to: "/jenever-discobowling", testid: "chapter-disco" },
  { num: "04", titleKey: "manifesto.c8t", textKey: "manifesto.c8x", img: IMAGES.kidsDisco, to: "/kinderfeestjes", testid: "chapter-kids-disco" },
  { num: "05", titleKey: "manifesto.c9t", textKey: "manifesto.c9x", img: IMAGES.kidBall, to: "/kinderfeestjes", testid: "chapter-kids" },
  { num: "06", titleKey: "manifesto.c5t", textKey: "manifesto.c5x", img: IMAGES.poolImg, to: "/bowling", testid: "chapter-pool" },
  { num: "07", titleKey: "manifesto.c6t", textKey: "manifesto.c6x", img: IMAGES.dartsImg, to: "/bowling", testid: "chapter-darts" },
  { num: "08", titleKey: "manifesto.c7t", textKey: "manifesto.c7x", img: IMAGES.cocktailsBar, to: "/restaurant", testid: "chapter-cocktailbar" },
];

const Manifesto = () => {
  const { t } = useLanguage();
  return (
    <section className="max-w-7xl mx-auto px-5 py-24" data-testid="manifesto">
      <SectionHeading eyebrow={t("manifesto.eyebrow")} title="Helios" />
      <div className="space-y-24">
        {chapters.map((c, i) => (
          <Reveal key={c.num}>
            <div
              data-testid={c.testid}
              className={`grid lg:grid-cols-2 gap-10 items-center ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}
            >
              <div className="relative overflow-hidden rounded-2xl group">
                <div className="absolute inset-0 bg-gradient-to-t from-[#05070B]/70 to-transparent z-10" />
                <img
                  src={c.img}
                  alt={t(c.titleKey)}
                  className="w-full h-72 sm:h-96 object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute top-4 left-5 z-20 font-disco text-5xl text-outline">{c.num}</span>
              </div>
              <div className={i % 2 ? "lg:text-right" : ""}>
                <span className="font-disco text-6xl sm:text-7xl text-outline block">{c.num}</span>
                <h3 className="font-display text-4xl sm:text-5xl uppercase neon mt-2">{t(c.titleKey)}</h3>
                <p className={`mt-4 text-[#9db8d6] leading-relaxed max-w-lg ${i % 2 ? "lg:ml-auto" : ""}`}>{t(c.textKey)}</p>
                <Link
                  to={c.to}
                  data-testid={`${c.testid}-link`}
                  className={`mt-6 inline-flex items-center gap-2 font-display tracking-[0.2em] uppercase text-[#00F0FF] border-b border-[#00F0FF]/40 pb-1 hover:gap-4 transition-all ${i % 2 ? "lg:flex-row-reverse" : ""}`}
                >
                  {t("manifesto.link")} <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const News = () => {
  const { t } = useLanguage();
  const items = [
    { tag: t("news.n1tag"), title: t("news.n1t"), date: t("news.n1d"), text: t("news.n1x"), img: IMAGES.newsBowling, testid: "news-bowling" },
    { tag: t("news.n2tag"), title: t("news.n2t"), date: t("news.n2d"), text: t("news.n2x"), img: IMAGES.newsBar, testid: "news-bar" },
  ];
  return (
    <section className="max-w-7xl mx-auto px-5 py-24" data-testid="news-section">
      <SectionHeading eyebrow="Helios" title={t("news.title")} sub={t("news.sub")} />
      <div className="grid md:grid-cols-2 gap-8">
        {items.map((n, i) => (
          <Reveal key={n.testid} delay={i * 0.12}>
            <article data-testid={n.testid} className="glass glow-card rounded-3xl overflow-hidden h-full flex flex-col">
              <div className="relative h-64 overflow-hidden group">
                <img src={n.img} alt={n.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E17] to-transparent" />
                <span className="absolute top-4 left-4 text-xs font-bold uppercase tracking-widest text-[#05070B] bg-[#F0C971] px-3 py-1 rounded-full">
                  {n.tag}
                </span>
              </div>
              <div className="p-8 flex-1 flex flex-col">
                <p className="font-display text-xs tracking-[0.3em] uppercase text-[#00F0FF]">{n.date}</p>
                <h3 className="font-display text-2xl sm:text-3xl uppercase neon mt-2">{n.title}</h3>
                <p className="mt-3 text-sm text-[#9db8d6] leading-relaxed">{n.text}</p>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const XmasCountdown = () => {
  const { t } = useLanguage();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const year = now.getFullYear();
  const sint = new Date(year, 11, 6);
  const xmas = new Date(year, 11, 25);
  const phase = now < sint ? "sint" : now < xmas ? "xmas" : "merry";
  const target = phase === "sint" ? sint : xmas;
  const diff = Math.max(0, target - now);
  const parts = [
    Math.floor(diff / 86400000),
    Math.floor(diff / 3600000) % 24,
    Math.floor(diff / 60000) % 60,
    Math.floor(diff / 1000) % 60,
  ];
  const labels = [t("xmas.days"), t("xmas.hours"), t("xmas.minutes"), t("xmas.seconds")];

  return (
    <section className="max-w-4xl mx-auto px-5 -mt-16 relative z-10 pb-28" data-testid="xmas-countdown">
      <Reveal>
        <div className="glass rounded-3xl px-6 py-8 sm:px-10 text-center relative overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#D4242A] via-[#F0C971] to-[#4ADE80]" />
          <h2 className="font-display text-xl sm:text-2xl uppercase tracking-widest text-[#F0C971]">{t("xmas.title")}</h2>
          <p className="text-sm text-[#9db8d6] mt-2 max-w-lg mx-auto">{t("xmas.sub")}</p>
          {phase !== "merry" && (
            <div className="mt-6 flex items-center justify-center gap-2 sm:gap-5">
              {parts.map((v, i) => (
                <div key={i} className="min-w-[62px] sm:min-w-[80px] rounded-2xl border border-[#F0C971]/25 bg-[#05070B]/60 px-3 py-3" data-testid={`xmas-count-${i}`}>
                  <p className="font-display text-2xl sm:text-4xl text-[#E2F1FF] tabular-nums">{String(v).padStart(2, "0")}</p>
                  <p className="text-[10px] uppercase tracking-widest text-[#9db8d6] mt-1">{labels[i]}</p>
                </div>
              ))}
            </div>
          )}
          <p className="mt-5 font-display text-sm sm:text-base tracking-[0.3em] uppercase neon" data-testid="xmas-count-label">
            {phase === "sint" ? t("xmas.toSint") : phase === "xmas" ? t("xmas.toXmas") : t("xmas.merry")}
          </p>
        </div>
      </Reveal>
    </section>
  );
};

const InstaFeed = () => {
  const { t } = useLanguage();

  useEffect(() => {
    if (!INSTAGRAM_POSTS.length) return;
    const process = () => window.instgrm?.Embeds?.process();
    if (!document.getElementById("instagram-embed-script")) {
      const s = document.createElement("script");
      s.id = "instagram-embed-script";
      s.async = true;
      s.src = "https://www.instagram.com/embed.js";
      s.onload = process;
      document.body.appendChild(s);
    } else {
      process();
    }
  }, []);

  return (
    <section className="max-w-7xl mx-auto px-5 py-24" data-testid="insta-section">
      <SectionHeading
        eyebrow={<span className="inline-flex items-center gap-2"><Instagram size={14} /> Instagram</span>}
        title={t("insta.title")}
        sub={t("insta.sub")}
      />
      {INSTAGRAM_POSTS.length > 0 && (
        <div className="flex flex-wrap justify-center gap-6 mb-12">
          {INSTAGRAM_POSTS.slice(0, 6).map((url, i) => (
            <Reveal key={url} delay={i * 0.08}>
              <div className="glass glow-card rounded-3xl p-3 overflow-hidden" data-testid={`insta-post-${i}`}>
                <blockquote
                  className="instagram-media"
                  data-instgrm-permalink={url}
                  data-instgrm-version="14"
                  style={{ maxWidth: 320, minWidth: 280, margin: 0, background: "#fff", borderRadius: 12 }}
                />
              </div>
            </Reveal>
          ))}
        </div>
      )}
      <Reveal delay={0.1}>
        <div className="text-center">
          <a
            href="https://www.instagram.com/bowlinghelios"
            target="_blank"
            rel="noreferrer"
            data-testid="insta-follow-button"
            className="btn-neon font-display text-sm tracking-[0.25em] uppercase px-10 py-4 rounded-full inline-flex items-center gap-3"
          >
            <Instagram size={17} /> {t("insta.follow")}
          </a>
        </div>
      </Reveal>
    </section>
  );
};

const Reviews = () => {
  const { t } = useLanguage();
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/reviews`).then((r) => setData(r.data)).catch(() => {});
  }, []);

  const items = data?.reviews || t("reviews.items");
  const isGoogle = data?.source === "google";
  return (
    <section className="max-w-7xl mx-auto px-5 py-24" data-testid="reviews">
      <SectionHeading title={t("reviews.title")} />
      {isGoogle && (
        <p className="text-center -mt-6 mb-8 text-sm text-[#9db8d6]" data-testid="reviews-google-badge">
          {t("reviews.viaGoogle")} · <span className="text-[#F0C971] font-bold">★ {data.rating}</span> ({data.count})
        </p>
      )}
      <div className="grid md:grid-cols-3 gap-6">
        {items.slice(0, 3).map((r, i) => (
          <Reveal key={i} delay={i * 0.12}>
            <div data-testid={`review-card-${i}`} className="glass glow-card rounded-2xl p-8 h-full flex flex-col">
              <Quote className="text-[#F0C971] mb-4" size={28} />
              <p className="text-[#E2F1FF] leading-relaxed flex-1">{r.text}</p>
              <div className="mt-6 flex items-center justify-between gap-2">
                <span className="font-display tracking-widest uppercase text-sm text-[#00F0FF]">{r.author}</span>
                <span className="flex gap-0.5 shrink-0">
                  {[...Array(r.rating || 5)].map((_, s) => (
                    <Star key={s} size={13} className="fill-[#F0C971] text-[#F0C971]" />
                  ))}
                </span>
              </div>
              {r.relativeTime && <p className="mt-2 text-xs text-[#9db8d6]">{r.relativeTime}</p>}
            </div>
          </Reveal>
        ))}
      </div>
      {isGoogle && data.mapsUrl && (
        <p className="text-center mt-8">
          <a href={data.mapsUrl} target="_blank" rel="noreferrer" data-testid="reviews-maps-link"
            className="text-sm text-[#00F0FF] border-b border-[#00F0FF]/40 pb-1 hover:text-[#E2F1FF] transition-colors">
            {t("reviews.allOnMaps")}
          </a>
        </p>
      )}
    </section>
  );
};

const HoursPreview = () => {
  const { t } = useLanguage();
  return (
    <section className="max-w-4xl mx-auto px-5 py-24" data-testid="hours-preview">
      <SectionHeading eyebrow={<span className="inline-flex items-center gap-2"><Clock size={14} /> Helios</span>} title={t("hours.title")} />
      <Reveal>
        <div className="glass rounded-2xl divide-y divide-[#00F0FF]/10">
          {HOURS.map((h) => (
            <div key={h.day} data-testid={`hours-${h.day}`} className="flex justify-between items-center px-6 sm:px-10 py-4">
              <span className="font-display text-lg tracking-widest uppercase text-[#E2F1FF]">{t(`hours.${h.day}`)}</span>
              <span className={h.time ? "text-[#00F0FF] font-semibold" : "text-[#F0C971] font-semibold"}>
                {h.time || t("hours.closed")}
              </span>
            </div>
          ))}
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="mt-6 text-center text-sm text-[#9db8d6]">{t("hours.groups")}</p>
        <p className="mt-2 text-center text-sm text-[#F0C971]">{t("hours.holidays")}</p>
      </Reveal>
    </section>
  );
};

export default function Home() {
  return (
    <main>
      <Hero />
      <XmasCountdown />
      <QuickInfo />
      <Marquee />
      <Manifesto />
      <News />
      <InstaFeed />
      <Reviews />
      <HoursPreview />
    </main>
  );
}
